/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from 'react';

import { Hco, Hcp } from '@/core/graphql/generated/types';
import { isOfTypeHco } from '@/utils/hco/isOfTypeHco';
import { useHcoUrlParams } from '@/utils/hco/useHcoUrlParams';

import {
	GetHcoByIdQuery,
	useGetHcoByIdLazyQuery
} from '../_graphql/GetHCOById.hook';

export type HcoProfile = Hco & {
	parentAffiliations: Hco['affiliations'];
	childAffiliations: Hco['affiliations'];
	parentInactiveAffiliationCount: Hco['inactiveAffiliationCount'];
	childInactiveAffiliationCount: Hco['inactiveAffiliationCount'];
};

const DEFAULT_AFFILIATIONS_PAGE_SIZE = 20;

export const useHcoData = (
	suppressErrors: boolean = false,
	affiliationsPageSize: number = DEFAULT_AFFILIATIONS_PAGE_SIZE,
	enabled: boolean = true
) => {
	const { id } = useHcoUrlParams(suppressErrors);

	const [getHcoById, { data, loading: queryLoading }] =
		useGetHcoByIdLazyQuery();
	const [sessionData, setSessionData] = useState<GetHcoByIdQuery | undefined>();

	useEffect(() => {
		if (id && enabled) {
			setSessionData(undefined);
			getHcoById({
				fetchPolicy: 'no-cache',
				variables: { vid: id, affiliationsPageSize }
			});
		} else if (!enabled) {
			setSessionData(undefined);
		}
	}, [getHcoById, id, affiliationsPageSize, enabled]);

	useEffect(() => {
		if (enabled && data) setSessionData(data);
	}, [enabled, data]);

	const loading = enabled && (queryLoading || (!!id && !sessionData));
	const hco = sessionData?.hco;

	const affiliateHcpCount =
		sessionData?.hcpSearchAndFilters?.__typename === 'HCPSearchAndFilters'
			? sessionData.hcpSearchAndFilters.search.__typename === 'HCPSearchResults'
				? sessionData.hcpSearchAndFilters.search.pagination.total
				: 0
			: 0;

	const affiliatedHcps = (
		sessionData?.hcpSearchAndFilters?.__typename === 'HCPSearchAndFilters'
			? sessionData.hcpSearchAndFilters.search.__typename === 'HCPSearchResults'
				? sessionData.hcpSearchAndFilters.search.results.map(h => h.hcp)
				: []
			: []
	) as Hcp[];

	if (
		!loading &&
		hco &&
		'__typename' in hco &&
		hco.__typename === 'ResourceNotFound'
	) {
		return {
			hco: null,
			loading,
			isInvalidHco: true
		};
	}

	return {
		hco: isOfTypeHco(hco) ? (hco as unknown as HcoProfile) : null,
		affiliateHcpCount,
		affiliatedHcps,
		loading,
		isInvalidHco: false
	};
};
